import applyWifiStyles from './styles';
import * as mqtt_instance from '@src/Main/mqtt_instance';

// NOTE: those are singletons, each app is responsible to draw to the left and right pane
// NOTE: they communicate via gApps to deselect all others apps and to select themselves
import networkApp from './networkEthernetApp.js';
import controllerApp from './networkControllersApp.js';
import wifiApp from './networkWiFiApp.js';
import * as machinemotion from '@vse/api/machinemotion.js';
import MachineMotionApi from '@lib/networkApi.js';

applyWifiStyles();

// this is mainly to allow each app to be selected/deselected without knowledge of other apps
// currently, the common interface is select/deselect
const gApps = {
  apps: [],
  add: function (pApp) {
    this.apps.push(pApp);
  },
  select: function (pAppName) {
    // NOTE: deselect all apps, and select the right one. That also means that an apps is only select via this call
    this.apps
      .filter(function (pApp) { return pApp.name != pAppName; })
      .forEach(function (pApp) { pApp.deselect(); });

    this.apps.find(function (pApp) { return pApp.name == pAppName; }).select();
  },
};

function getControllersInfos (completion) {
  // Fetch the Library to search for #global application
  machinemotion.application('#global', function (pApp) {
    if (!pApp || !pApp.sequences || !pApp.sequences.length > 0) { return completion(null); }
    const lStartup = pApp.sequences.find(function (pSequence) { return pSequence.name == '#startup'; });
    if (!lStartup && !lStartup.instructions) { return completion(null); }

    const parentUUID = lStartup.parameters.parentController;
    const controllers = lStartup.instructions
      .filter(instruction => { return instruction.name == 'controller'; })
      .map(instruction => { return { name: instruction.parameters.friendlyName, uuid: instruction.parameters.uuid }; })
      .sort((a, b) => {
        // put the parent first
        if (a.uuid == parent) { return -1; }
        if (b.uuid == parent) { return 1; }
        return 0;
      })
      .reduce((accumulator, current) => {
        // build a map
        accumulator[current.uuid] = { name: current.name, parent: current.uuid == parentUUID };
        return accumulator;
      }, {});
    completion(controllers);
  });
}

function initMqttClient () {
  const wifiAvailable = 'devices/pendant/available';
  const wifiAvailableRegex = new RegExp(wifiAvailable);

  let wifiAppPollingCancellation;
  function onWifiAvailable (topic, message) {
    wifiApp.update('onAvailable', JSON.parse(message));
    wifiAppPollingCancellation = () => wifiApp.cancelPolling();
  }

  let mqttClient;
  const mqttGetClientCancellation = mqtt_instance.getClient(client => {
    mqttClient = client;
    mqttClient.subscribe(wifiAvailable, wifiAvailableRegex, onWifiAvailable);
  });

  return () => {
    mqttGetClientCancellation();
    mqttClient?.unsubscribe(wifiAvailable, wifiAvailableRegex, onWifiAvailable);
    wifiAppPollingCancellation?.();
  };
}

function getAndUpdateDetectedNetworks () {
  // Multi Controller discovery
  machinemotion.getDetectedNetwork(
    (networkDiscovered) => { controllerApp.update('onDiscovered', networkDiscovered.detectedControllers); },
  );
}

function createGenericUI () {
  const lGenericContainer = $('#guiContainer').css({ display: 'flex' });

  const left = $('<div id="left_pane">')
    .addClass('scrollbar')
    .css({
      overflow: 'hidden',
      'overflow-y': 'auto',
      'margin-right': '10px',
      'padding-right': '2px',
      width: '500px',
      // Does not work on the PENDANT 'scrollbar-gutter': 'stable', // if you want it to keep the same width when the scroll bar appears
    });

  const right = $('<div id="right_pane">')
    .css({
      'box-sizing': 'border-box',
      border: '3px solid #EFF2F7',
      'border-radius': '3px',
      display: 'flex',
      'align-items': 'center',
      'justify-content': 'center',
    });

  lGenericContainer.append(left, right);
}

/**
 * Polls the specified network at a set interval.
 * Polling will continue on error.
 *
 * @param {NetworkNames} networkName The network to poll.
 * @param {number} [intervalMs] The interval in milliseconds between requests. Defaults to 5 seconds.
 * @return {() => void} A function to stop polling.
 */
function pollNetworkInfo (networkName, intervalMs = 5000) {
  let networkInfoRequestCancellation = null;
  let requestTimeout = null;

  function poll () {
    networkInfoRequestCancellation = MachineMotionApi.getNetworkInfo(
      networkName,
      jsonResponse => {
        networkApp.update(networkName, JSON.parse(jsonResponse));
        requestTimeout = setTimeout(poll, intervalMs);
      },
      () => {
        requestTimeout = setTimeout(poll, intervalMs);
      },
    );
  }

  poll();

  return () => {
    networkInfoRequestCancellation?.();
    clearTimeout(requestTimeout);
  };
}

/**
 * @param {Navigate} navigate A function to navigate to another page.
 * @returns {() => void} Cleanup logic for when we leave the page.
 */
export function initializeNetworkConfig (navigate) {
  const cleanups = [];

  createGenericUI();

  const leftPane = $('#left_pane');
  const rightPane = $('#right_pane');

  // initialize the Apps
  controllerApp.init(leftPane, rightPane, gApps, { getDetectedNetworks: getAndUpdateDetectedNetworks });
  gApps.add(controllerApp);

  networkApp.init(leftPane, rightPane, gApps, { isConnected: () => wifiApp.isAvailable() });
  gApps.add(networkApp);

  wifiApp.init(leftPane, rightPane, gApps);
  gApps.add(wifiApp);

  const lUrl = window.location;
  const urlParams = new URLSearchParams(lUrl.search);
  if (urlParams.has('selectPage') && (urlParams.get('selectPage') === 'wifi')) {
    gApps.select('Available WiFi Networks');
    wifiApp.collapse(wifiApp.uiLeft);

    // We want to remove url params from the path because we handled them
    navigate(lUrl.pathname, { replace: true });
  }
  else {
    // show default
    gApps.select('MachineMotion Controllers');
  }

  getControllersInfos((data) => {
    if (data) { controllerApp.update('onControllersName', data); }
  });

  // Note : This is a kludge. we will delay the call to server to allow all ressource to load
  // Because getting the IP, Gateways and Netmasks will make call to child_process.execSync(), which is long and blocking..
  // This will lock everything until the calls finishes, meaning that any files (icons/fonts/images) being loaded will stall.
  // This will/might result in a partial UI rendered (until the exec finishes), thus the timeout.
  const initializationTimeout = setTimeout(function () {
    cleanups.push(initMqttClient());
    getAndUpdateDetectedNetworks();
    cleanups.push(pollNetworkInfo('LAN_1'));
    cleanups.push(pollNetworkInfo('LAN_2'));
  }, 200);

  return () => {
    clearTimeout(initializationTimeout);
    cleanups.forEach(cleanup => cleanup());
  };
}
